// Keep in step with the required-field asterisks of the Client tab (modulesConfig, Contact.jsx, address fields).

type FieldValue = string | number | boolean | null | undefined

export interface CustomerRules {
  siret?: boolean
  socialReasonMandatory?: boolean
  secondName?: boolean
  referral?: boolean
  bornCity?: boolean
  birthdateMandatory?: boolean
  taxIdCode?: boolean
}

export interface IdentityValues {
  consent: boolean
  isProfessional: boolean
  clientType?: FieldValue
  civility?: FieldValue
  lastName?: FieldValue
  firstName?: FieldValue
  phone1?: FieldValue
  birthdate?: FieldValue
  bornCity?: FieldValue
  taxIdCode?: FieldValue
  siret?: FieldValue
  socialReason?: FieldValue
  secondName?: boolean
  civility2?: FieldValue
  lastName2?: FieldValue
  firstName2?: FieldValue
  referral?: boolean
  referralFirstName?: FieldValue
  referralLastName?: FieldValue
}

export interface AddressValues {
  hasAddressesButNoneSelected: boolean
  zipcode?: FieldValue
  city?: FieldValue
  residenceType?: FieldValue
  housingType?: FieldValue
  tenantType?: FieldValue
  isResident?: FieldValue
  showResidenceType: boolean
  showHousingType: boolean
  showTenantType: boolean
  showIsResident: boolean
  optionalResidenceFields: boolean
}

export type IdentityField =
  | 'clientType'
  | 'civility'
  | 'lastName'
  | 'firstName'
  | 'phone1'
  | 'birthdate'
  | 'bornCity'
  | 'taxIdCode'
  | 'siret'
  | 'socialReason'
  | 'civility2'
  | 'lastName2'
  | 'firstName2'
  | 'referralFirstName'
  | 'referralLastName'

type ResidenceField = 'residenceType' | 'housingType' | 'tenantType' | 'isResident'

export type AddressField = 'addressChoice' | 'addressSearch' | ResidenceField

export type ClientTabField = IdentityField | AddressField

export const CONSUMPTION_FIELD_COUNT = 4

const isEmpty = (value: FieldValue) => value === undefined || value === null || String(value).trim() === ''

const countEmpty = (values: FieldValue[]) => values.filter(isEmpty).length

export const missingIdentityFields = (values: IdentityValues, rules: CustomerRules): IdentityField[] => {
  const required: IdentityField[] = ['clientType', 'civility', 'lastName', 'firstName', 'phone1']
  if (rules.birthdateMandatory) required.push('birthdate')
  if (rules.bornCity) required.push('bornCity')
  if (rules.taxIdCode) required.push('taxIdCode')
  if (rules.siret && values.isProfessional) required.push('siret')
  if (rules.socialReasonMandatory && values.isProfessional) required.push('socialReason')
  if (rules.secondName && values.secondName) required.push('civility2', 'lastName2', 'firstName2')
  if (rules.referral && values.referral) required.push('referralFirstName', 'referralLastName')

  return required.filter(field => isEmpty(values[field]))
}

export const countMissingIdentityFields = (values: IdentityValues, rules: CustomerRules) =>
  values.consent ? missingIdentityFields(values, rules).length : 1

export const missingAddressFields = (values: AddressValues): AddressField[] => {
  if (values.hasAddressesButNoneSelected) return ['addressChoice']

  const required: ResidenceField[] = []
  if (!values.optionalResidenceFields) {
    if (values.showResidenceType) required.push('residenceType')
    if (values.showHousingType) required.push('housingType')
    if (values.showTenantType) required.push('tenantType')
  }
  if (values.showIsResident) required.push('isResident')
  const missing = required.filter(field => isEmpty(values[field]))

  return isEmpty(values.zipcode) || isEmpty(values.city) ? ['addressSearch', ...missing] : missing
}

export const countFilledConsumptionFields = (values: FieldValue[]) => values.length - countEmpty(values)
